import React, { useMemo, useState, useEffect, useContext } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import DataTable from "../common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import CustomLoader from "../common/CustomLoader";
import { genericGet } from "../../services/apiServices";
import { GET_IMPORT_SHIPMENT_BOOKING_REPORT } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import { getColorById, getColorPlate, getPortMasters, getSuppliersList } from "../../services/CommonServices";
import { Box, Grid } from "@mui/material";
import CustomFilter from "../common/CustomFilter";
import CustomTextField from "../common/CustomTextField";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomButton from "../common/CustomButton";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import { genericPost } from "../../services/authServices";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomStatus from "../common/CustomStatus";

const title = "Import Booking Report";

const ImportBookingReport = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [permission, setPermissions] = useState()
    const [colorlist, setColorlist] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    const [portMasters, setPortMasters] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);

    const [filterPayload, setFilterPayload] = useState({
        shipmentId: "",
        clientCompanyId: "",
        portOfLoading: "",
        portOfDestination: "",
        fromDate: '',
        toDate: '',

    });
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])

    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: "",
                clientCompanyId: "",
                portOfLoading: "",
                portOfDestination: "",
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
        const getPortMastersData = await getPortMasters()
        setPortMasters(getPortMastersData);
        const colorData = await getColorPlate();
        setColorlist(colorData);
    }


    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'date', header: 'Date', size: 100, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: "bookingNumber", header: "Booking No.", size: 140,
                tableStyle: { textAlign: "center" },
            },
            { accessorKey: 'clientCompanyName', header: 'Supplier', size: 100, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'shipmentId', header: 'Shipment I’d', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'proformaInvoice', header: 'PI No.', size: 100, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: "portOfLoading",
                header: "Port Of Loading",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.portOfLoading}
                            sx={{
                                backgroundColor: row.original.portOfLoadingBackground,
                                color: row.original.portOfLoadingColor,
                                border: `1px solid ${row.original.portOfLoadingColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: "portOfDestination",
                header: "Port Of Destination",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.portOfDestination}
                            sx={{
                                backgroundColor: row.original.portOfDestinationBackground,
                                color: row.original.portOfDestinationColor,
                                border: `1px solid ${row.original.portOfDestinationColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'etdDate', header: 'etd Date', size: 150, tableStyle: { textAlign: "center" } },
            { accessorKey: 'etaDate', header: 'eta Date', size: 150, tableStyle: { textAlign: "center" } },
            {
                accessorKey: 'cifOrFob', header: 'cif and Fob',
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.cifOrFob}
                            sx={{
                                backgroundColor: row.original.cifFobBackground,
                                color: row.original.cifFobColor,
                                border: `1px solid ${row.original.cifFobColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'seaFreight', header: 'sea Freight Per container', size: 150, tableStyle: { textAlign: "center" } },
            { accessorKey: 'otherCharges', header: 'other Charges', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shippingLineCharges', header: 'shipping Line Charges', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'blCharges', header: 'bl Charges', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'noOfContainers', header: 'no Of Containers', size: 150, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'shippingLine', header: 'shipping Line',
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.shippingLine}
                            sx={{
                                backgroundColor: row.original.shippingLineBackground,
                                color: row.original.shippingLineColor,
                                border: `1px solid ${row.original.shippingLineColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: 'freightForwardAgency', header: 'freight Forward Agency',
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.freightForwardAgency}
                            sx={{
                                backgroundColor: row.original.freightForwardBackground,
                                color: row.original.freightForwardColor,
                                border: `1px solid ${row.original.freightForwardColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: 'clearingAgency', header: 'clearing Agency',
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.clearingAgency}
                            sx={{
                                backgroundColor: row.original.clearingAgencyBackground,
                                color: row.original.clearingAgencyColor,
                                border: `1px solid ${row.original.clearingAgencyColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: 'shippingStatus', header: 'Shipment Status',
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.shippingStatus}
                            sx={{
                                backgroundColor: row.original.shippingStatusBackground,
                                color: row.original.shippingStatusColor,
                                border: `1px solid ${row.original.shippingStatusColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
        ],
        [permission]
    );
    const formatPortName = (port, country) => {
        return [port, country].filter(Boolean).join(", ");
    };

    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        setError('');
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };
            const response = await genericPost(GET_IMPORT_SHIPMENT_BOOKING_REPORT, payload, {});
            if (response.data.success && Array.isArray(response.data.shippingBooking)) {
                const shippingData = response.data.shippingBooking.map((item, index) => {

                    const countryColorObj = getColorById(item.importShipment?.supplier?.billingCountry?.id, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });
                    const shippingStatusColorObj = getColorById(item?.shippingTracker?.shippingStatus?.shippingStatusId, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });

                    return {
                        index: (index + 1).toString().padStart(2, '0'),
                        shipmentId: item.shipmentId || '',
                        proformaInvoice: item?.ProformaInvoice?.proformaInvoiceNo || '',
                        date: companyDateFormat(item.createdAt) || '',
                        etaDate: companyDateFormat(item.etaDate) || '',
                        etdDate: companyDateFormat(item.etdDate) || '',
                        cifOrFob: item.cifFob?.cifFobName || '',
                        seaFreight: item.currency?.currency_symbol
                            ? `${item.currency.currency_symbol} ${Number(item.seaFreight).toFixed(2)}`
                            : item.seaFreight,
                        otherCharges: item.baseCurrency?.currency_symbol
                            ? `${item.baseCurrency.currency_symbol} ${Number(item.destinationCharges || 0 + item.cfsLocalCharges || 0).toFixed(2)}`
                            : '',
                        shippingLineCharges: item.baseCurrency?.currency_symbol
                            ? `${item.baseCurrency.currency_symbol} ${Number(item.shippingLineCharges).toFixed(2)}`
                            : '',
                        blCharges: item.baseCurrency?.currency_symbol
                            ? `${item.baseCurrency.currency_symbol} ${Number(item.blCharges).toFixed(2)}`
                            : '',
                        noOfContainers: item.numberOfContainers || '',
                        shippingLine: item.shippingLines?.shippingLineName || '',
                        freightForwardAgency: item.freightForwardAgency?.agencyName || '',
                        clearingAgency: item.customClearingAgency?.chaName || '',
                        clientCompanyName: item.importShipment?.supplier?.clientCompanyName || '',
                        billingCountry: item.importShipment?.supplier?.billingCountry?.name || '',
                        shippingStatus: item?.shippingTracker?.shippingStatus?.title || '',
                        portOfLoading: formatPortName(
                            item?.loading?.portName,
                            item?.loading?.country?.name
                        ),
                        portOfDestination: formatPortName(
                            item?.destination?.portName,
                            item?.destination?.country?.name
                        ),
                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',

                        portOfLoadingColor: item?.loading?.ColorPlate?.color || '',
                        portOfLoadingBackground: item?.loading?.ColorPlate?.backgroundColor || '',

                        portOfDestinationColor: item?.destination?.ColorPlate?.color || '',
                        portOfDestinationBackground: item?.destination?.ColorPlate?.backgroundColor || '',

                        cifFobColor: item?.cifFob?.ColorPlate.color || '',
                        cifFobBackground: item?.cifFob?.ColorPlate.backgroundColor || '',

                        shippingLineColor: item?.shippingLines?.ColorPlate.color || '',
                        shippingLineBackground: item?.shippingLines?.ColorPlate.backgroundColor || '',

                        freightForwardColor: item?.freightForwardAgency?.ColorPlate.color || '',
                        freightForwardBackground: item?.freightForwardAgency?.ColorPlate.backgroundColor || '',

                        clearingAgencyColor: item?.customClearingAgency?.ColorPlate.color || '',
                        clearingAgencyBackground: item?.customClearingAgency?.ColorPlate.backgroundColor || '',

                        shippingStatusColor: shippingStatusColorObj.color || '',
                        shippingStatusBackground: shippingStatusColorObj.backgroundColor || '',
                        bookingNumber: item.bookingNumber || '',


                    };
                });
                setData(shippingData);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setError('No shipping bookings found.');
            }
        } catch (error) {
            console.error("Fetch error:", error);
            setError('Failed to fetch shipping bookings.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, token, colorlist])

    const defaultView = 'importbookingreport';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("clientCompanyId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.clientCompanyId) || null}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={portMasters}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Port Of Loading"
                                        onChange={(event, newValue) => handleFilterChange("portOfLoading", newValue?.id)}
                                        value={portMasters.find((item) => item.id === filterPayload.portOfLoading) || null}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={portMasters}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Port Of Destination"
                                        onChange={(event, newValue) => handleFilterChange("portOfDestination", newValue?.id)}
                                        value={portMasters.find((item) => item.id === filterPayload.portOfDestination) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );

    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };


    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}

        </>
    );
};
export default ImportBookingReport;
